<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { useToast } from 'vue-toastification';
  import { UiModalHeader, PrintButton, ThemeProvider } from '@/shared/ui';
  import { useBranchLogo } from '@/entities/Branch';
  import { getGroupedDistributions } from '../../api/distribution-list.api';
  import type { DocumentDistributionGroupedData } from '../../model/distribution.types';
  import DistributionListBanner from './DistributionListBanner.vue';
  import DistributionListContent from './DistributionListContent.vue';
  import { DISTRIBUTION_LIST_PRINT_STYLES } from './distribution-list-print-styles';

  const MODAL_TITLE = 'Controle de Distribuição';

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const handleClose = () => {
    emit('close');
  };

  const toast = useToast();
  const { resolvePrintableLogoSrc } = useBranchLogo();
  const isLoading = ref(true);
  const currentDate = ref('');
  const distributionData = ref<DocumentDistributionGroupedData>({
    qualityManual: [],
    procedure: [],
    workInstruction: [],
    supportDocument: [],
    externalDocument: [],
  });

  function formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  onMounted(async () => {
    currentDate.value = formatDate(new Date());
    isLoading.value = true;
    try {
      distributionData.value = await getGroupedDistributions();
    } catch {
      toast.error('Erro ao carregar lista de distribuição.');
    } finally {
      isLoading.value = false;
    }
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-19/20 max-h-[90vh] overflow-hidden"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="flex flex-col flex-1 min-h-0 overflow-hidden">
      <UiModalHeader
        class="px-6 py-4 text-sm border-b border-gray-02"
        :title="MODAL_TITLE"
        @close="handleClose"
      />
      <template v-if="isLoading">
        <div class="flex justify-center items-center py-12">
          <span class="text-gray-500 text-sm">Carregando...</span>
        </div>
      </template>
      <template v-else>
        <div
          id="modal-content"
          class="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          <DistributionListBanner
            class="flex-none"
            :date="currentDate"
          />
          <PrintButton
            class="pt-6 flex-none"
            :title="MODAL_TITLE"
            :print-styles="DISTRIBUTION_LIST_PRINT_STYLES"
            :resolve-logo-src="resolvePrintableLogoSrc"
          />
          <div class="overflow-y-auto flex-1 min-h-0">
            <DistributionListContent :data="distributionData" />
          </div>
        </div>
      </template>
    </ThemeProvider>
  </VueFinalModal>
</template>
